/**
 * WineCard.js — Wine listing card (cardType "wine_listing").
 *
 * THE shared "rich winery" wine card, used by the tenants whose old design was
 * the same (Halleck, Hestan — byte-identical copies). DOM:
 *   [discount badge?] → [SOLD OUT overlay] → image (clickable) → "Tasting notes"
 *   → title → optional meta (country | alcohol | type) → footer{ price + unit,
 *   FULL-WIDTH add-to-cart }.
 * Colours come from the shared cards.css (themed via --ws-rich-* / admin theme);
 * a tenant overrides a rule in its own styles.css ONLY where it differs.
 *
 * A tenant needs its OWN WineCard only when its DOM genuinely differs — Goose
 * (content-sized BUY NOW row), Valle (in-widget Commerce7 cart), Fogarty,
 * Wine Engine (Add to Basket + purchase row).
 *
 * unique_id is forwarded on click so the BE returns this wine's detail card
 * (handle_unique_id_lookup) instead of an LLM text answer.
 *
 * Reference: halleck_vineyard/script.js Lines 1917-1969 (createWineListing);
 *   wineengine main.js createWineListing (discount + meta).
 */

import { emit, isChatDisabled } from "../../core/state.js";
import { bindWineDrawerOpen } from "../../utils/openWineDrawer.js";

const TASTING_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none" aria-hidden="true"><path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

function parseMoney(value) {
  if (value == null || value === "") return NaN;
  return parseFloat(String(value).replace(/[^0-9.]/g, ""));
}

function discountPercent(wine) {
  const orig = parseMoney(wine.compare_at_price);
  const curr = parseMoney(wine.regular_price);
  if (!(orig > curr && curr > 0)) return 0;
  return Math.round((1 - curr / orig) * 100);
}

function metaLine(wine) {
  const notes = wine.winemaker_notes || {};
  const items = [
    wine.country,
    notes.Alcohol || notes.alcohol || "",
    wine.wine_type || "",
  ].filter(Boolean);
  return items.length ? items.join(" | ") : "";
}

export default class WineCard {
  render(wine) {
    const card = document.createElement("div");
    card.className = "ws-card ws-wine-card ws-rich-winecard";
    card.setAttribute("data-variant-id", wine.variant_id ?? wine["Variant SKU"] ?? "");
    // External-shop tenants (Halleck, etc.): tasting-notes drawer footer uses
    // this for "Buy Now" + Cancel — same as Hestan/Goose WineCard data-cta-link.
    if (wine.cta_link) card.dataset.ctaLink = wine.cta_link;
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const detailLabel = wine.message || `Tell me more about ${wine.title || "this wine"}`;
    const openDetail = () =>{

      if(isChatDisabled()) return;
      emit("ws-prompt-select", {
        label: detailLabel,
        value: detailLabel,
        uniqueId: wine.unique_id,
      });
    }

    const pct = !soldOut ? discountPercent(wine) : 0;
    if (pct > 0) {
      const badge = document.createElement("div");
      badge.className = "ws-rich-discount";
      badge.innerHTML = `<span class="ws-rich-discount-pct">-${pct}%</span>`;
      card.appendChild(badge);
    }

    if (soldOut) {
      const overlay = document.createElement("div");
      overlay.className = "ws-rich-soldout";
      overlay.textContent = "SOLD OUT";
      card.appendChild(overlay);
    }

    const head = document.createElement("div");
    head.className = "ws-card-head ws-rich-head";
    if (wine.cover_image_link) {
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = wine.cover_image_link;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      img.style.cursor = "pointer";
      img.addEventListener("click", openDetail);
      head.appendChild(img);
    }
    card.appendChild(head);

    const body = document.createElement("div");
    body.className = "ws-card-body ws-rich-body";

    if (!soldOut) {
      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-rich-tasting";
      tasting.innerHTML = `${TASTING_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, wine, card);
      body.appendChild(tasting);
    }

    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title ws-rich-winetitle";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    const meta = metaLine(wine);
    if (meta) {
      const desc = document.createElement("p");
      desc.className = "ws-rich-meta";
      desc.textContent = meta;
      body.appendChild(desc);
    }

    const footer = document.createElement("div");
    footer.className = "ws-rich-footer";

    const priceRow = document.createElement("div");
    priceRow.className = "ws-rich-pricerow";
    if (wine.regular_price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = wine.regular_price;
      priceRow.appendChild(price);
    }
    if (wine.bottle_quantity || wine.price_unit) {
      const qty = document.createElement("p");
      qty.className = "ws-card-unit";
      qty.textContent = wine.bottle_quantity || wine.price_unit;
      priceRow.appendChild(qty);
    }
    if (priceRow.children.length) footer.appendChild(priceRow);

    if (wine.cta_link && !soldOut && !unavailable) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta ws-rich-cta";
      cta.href = wine.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = wine.cta_text || "Add to cart";
      footer.appendChild(cta);
    }

    body.appendChild(footer);
    card.appendChild(body);
    return card;
  }
}
